<script lang="ts">
  import type { BeadColor } from '../catalog/types';
  import type { RenderMode } from '../render/types';
  import Icon from './Icon.svelte';

  /**
   * Panneau mobile étendu — bottom-sheet déclenchée depuis le bouton « Plus »
   * du `MobileDock`. Contient les options secondaires (mode d'affichage,
   * bords plaque, fond, actions de création, fit view) ainsi que le détail
   * de la couleur active en grand.
   *
   * Slide-up : `transform` animée pour entrer / sortir. Bloc le scroll de la
   * page en arrière-plan via `touch-action: none` sur l'overlay.
   */

  type CanvasBgMode = 'white' | 'brand' | 'custom';

  type Tool = 'paint' | 'bucket' | 'eraser' | 'pan';

  type Props = {
    open: boolean;
    projectName: string;
    mode: RenderMode;
    showPlateBorders: boolean;
    canvasBgMode: CanvasBgMode;
    canvasBgCustom: string;
    activeColor: BeadColor | undefined;
    /** Outil courant — la « couleur active » devient un filtre pour la gomme. */
    tool: Tool;
    onClose: () => void;
    onChangeProjectName: (name: string) => void;
    onSelectMode: (m: RenderMode) => void;
    onTogglePlateBorders: () => void;
    onSelectBgMode: (m: CanvasBgMode) => void;
    onChangeCustomBg: (hex: string) => void;
    onFitView: () => void;
    onExport: () => void;
    /** Ouvre le catalogue complet des perles Hama (référence visuelle). */
    onOpenBeadGuide: () => void;
    /** Bascule en mode pose guidée (assistant pour réaliser physiquement le patron). */
    onStartGuidedMode: () => void;
  };

  let {
    open,
    projectName,
    mode,
    showPlateBorders,
    canvasBgMode,
    canvasBgCustom,
    activeColor,
    tool,
    onClose,
    onChangeProjectName,
    onSelectMode,
    onTogglePlateBorders,
    onSelectBgMode,
    onChangeCustomBg,
    onFitView,
    onExport,
    onOpenBeadGuide,
    onStartGuidedMode,
  }: Props = $props();

  function onNameInput(e: Event): void {
    onChangeProjectName((e.currentTarget as HTMLInputElement).value);
  }

  let bgInputEl: HTMLInputElement | undefined = $state();

  function onCustomClick(): void {
    onSelectBgMode('custom');
    // Petit délai pour que le state se mette à jour avant d'ouvrir le picker natif
    setTimeout(() => bgInputEl?.click(), 0);
  }

  function onBgInputChange(e: Event): void {
    const v = (e.currentTarget as HTMLInputElement).value;
    onChangeCustomBg(v);
  }
</script>

{#if open}
  <button
    type="button"
    class="mobile-panel__backdrop"
    aria-label="Fermer le panneau"
    onclick={onClose}
  ></button>
{/if}

<aside
  class="mobile-panel editor-glass"
  class:is-open={open}
  aria-hidden={!open}
  aria-label="Options supplémentaires"
>
  <header class="mobile-panel__header">
    <span class="mobile-panel__handle" aria-hidden="true"></span>
    <h2 class="mobile-panel__title">Options</h2>
    <button
      type="button"
      class="mobile-panel__close"
      aria-label="Fermer"
      onclick={onClose}
    >
      ×
    </button>
  </header>

  <div class="mobile-panel__body">
    <!-- Nom du patron -->
    <section class="mobile-panel__section">
      <span class="mobile-panel__caption">Nom du patron</span>
      <input
        class="mobile-panel__name-input"
        type="text"
        value={projectName}
        oninput={onNameInput}
        placeholder="Sans titre"
        aria-label="Nom du patron"
        maxlength="80"
      />
    </section>

    <!-- Couleur active en évidence — version compacte : la sélection est
         visible dans la palette horizontale du dock, on n'a pas besoin
         d'une grosse répétition. Affiché en mode gomme uniquement quand
         pas de couleur (cas « toutes »), pour clarifier le mode. -->
    {#if tool === 'eraser' && !activeColor}
      <section class="mobile-panel__section mobile-panel__active-color">
        <span class="mobile-panel__caption">Filtre de la gomme</span>
        <div class="mobile-panel__active-row">
          <span
            class="editor-swatch__dot editor-swatch__dot--empty"
            style="--swatch-size: 28px"
            aria-hidden="true"
          ></span>
          <strong>Toutes couleurs · efface tout sous le pinceau</strong>
        </div>
      </section>
    {/if}

    <!-- Affichage des perles -->
    <section class="mobile-panel__section">
      <span class="mobile-panel__caption">Affichage</span>
      <div class="mobile-panel__row">
        <button
          type="button"
          class="editor-tool-chip"
          class:is-active={mode === 'pearl'}
          aria-pressed={mode === 'pearl'}
          onclick={() => onSelectMode('pearl')}
        >
          Perle
        </button>
        <button
          type="button"
          class="editor-tool-chip"
          class:is-active={mode === 'flat'}
          aria-pressed={mode === 'flat'}
          onclick={() => onSelectMode('flat')}
        >
          Grille
        </button>
        <button
          type="button"
          class="editor-tool-chip"
          class:is-active={showPlateBorders}
          aria-pressed={showPlateBorders}
          onclick={onTogglePlateBorders}
        >
          <span aria-hidden="true">{showPlateBorders ? '✓' : ''}</span>
          Bords plaque
        </button>
      </div>
    </section>

    <!-- Fond -->
    <section class="mobile-panel__section">
      <span class="mobile-panel__caption">Fond du canvas</span>
      <div class="mobile-panel__row">
        <button
          type="button"
          class="editor-tool-chip"
          class:is-active={canvasBgMode === 'white'}
          aria-pressed={canvasBgMode === 'white'}
          onclick={() => onSelectBgMode('white')}
        >
          Blanc
        </button>
        <button
          type="button"
          class="editor-tool-chip"
          class:is-active={canvasBgMode === 'brand'}
          aria-pressed={canvasBgMode === 'brand'}
          onclick={() => onSelectBgMode('brand')}
        >
          Brand
        </button>
        <button
          type="button"
          class="editor-tool-chip mobile-panel__bgpicker"
          class:is-active={canvasBgMode === 'custom'}
          aria-pressed={canvasBgMode === 'custom'}
          onclick={onCustomClick}
        >
          <span
            class="mobile-panel__bgswatch"
            style={`background: ${canvasBgCustom}`}
          ></span>
          Custom
        </button>
        <input
          bind:this={bgInputEl}
          type="color"
          value={canvasBgCustom}
          oninput={onBgInputChange}
          class="mobile-panel__bginput"
          aria-label="Couleur de fond personnalisée"
          tabindex="-1"
        />
      </div>
    </section>

    <!-- Vue / Actions sur le patron courant (la création est dans le FAB,
         pas de duplication). -->
    <section class="mobile-panel__section">
      <span class="mobile-panel__caption">Pose</span>
      <div class="mobile-panel__row">
        <button
          type="button"
          class="editor-tool-chip editor-tool-chip--accent"
          onclick={onStartGuidedMode}
        >
          <Icon name="play" size={16} /> Pose guidée
        </button>
        <button type="button" class="editor-tool-chip" onclick={onOpenBeadGuide}>
          <Icon name="star" size={16} /> Guide des perles
        </button>
      </div>
    </section>

    <section class="mobile-panel__section">
      <span class="mobile-panel__caption">Actions</span>
      <div class="mobile-panel__row">
        <button type="button" class="editor-tool-chip" onclick={onFitView}>
          <Icon name="maximize" size={16} /> Recadrer
        </button>
        <button type="button" class="editor-tool-chip" onclick={onExport}>
          <Icon name="download" size={16} /> Exporter PDF
        </button>
      </div>
    </section>
  </div>
</aside>

<style>
  /* Backdrop : intercepte le tap pour fermer le panneau */
  .mobile-panel__backdrop {
    position: fixed;
    inset: 0;
    background: rgb(20 32 44 / 0.35);
    border: 0;
    padding: 0;
    z-index: 30;
    cursor: pointer;
  }

  .mobile-panel {
    position: absolute;
    left: var(--space-2);
    right: var(--space-2);
    bottom: var(--space-2);
    z-index: 40;
    border-radius: var(--radius-md);
    transform: translateY(110%);
    transition: transform 0.2s ease-out;
    /* Adaptive : prend jusqu'à 85% du viewport mobile pour pouvoir afficher
       toutes les options sans scroll excessif. Le body interne scroll si
       le contenu déborde. */
    max-height: 85dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .mobile-panel.is-open {
    transform: translateY(0);
  }

  .mobile-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--color-border);
    position: relative;
  }
  .mobile-panel__handle {
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 3px;
    background: var(--color-border);
    border-radius: 2px;
  }
  .mobile-panel__title {
    font-size: var(--text-body);
    margin: 0;
    margin-top: var(--space-1);
  }
  .mobile-panel__close {
    background: transparent;
    border: 0;
    font-size: 24px;
    line-height: 1;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: var(--space-1) var(--space-2);
  }
  .mobile-panel__close:hover {
    color: var(--color-text);
  }

  .mobile-panel__body {
    overflow-y: auto;
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .mobile-panel__section {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .mobile-panel__caption {
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .mobile-panel__row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .mobile-panel__active-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-small);
  }

  .mobile-panel__bgpicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .mobile-panel__bgswatch {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid var(--color-border);
  }
  .mobile-panel__name-input {
    font-family: var(--font-heading);
    font-size: var(--text-h3);
    color: var(--color-text);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    padding: var(--space-2) 10px;
    width: 100%;
  }
  .mobile-panel__name-input:focus {
    outline: none;
    border-color: var(--color-accent);
  }
  .mobile-panel__bginput {
    /* Caché ; déclenché par le bouton "Custom" via onPickCustomBg côté hôte */
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }
</style>
